// Hermes domain — SquadCall: value objects do squad_call_log (Squad Orchestrator).
//
// PURO de verdade: zero imports, zero I/O, zero crypto (o hash sha-256 vive na
// infra — aqui só normalizamos a ENTRADA do hash, de forma determinística e
// estável). O architecture gate garante que continue assim.
//
// E2: promptHashInput (o que entra no hash — nunca timestamps/aleatório, ordem
//     estável) + estimateContextTokens.
// E3/E4 acrescentam deriveWorkerStatus + capDispatchContext neste mesmo módulo.

/**
 * Normaliza o material do prompt num string determinístico pro hash.
 * Mesma (stage, system, user) → mesmo input → mesmo hash. Sem timestamps.
 * O prompt CRU nunca sai daqui pro D1 — só o hash dele (infra).
 */
export function promptHashInput({ stage = '', system = '', user = '' } = {}) {
  return `${String(stage)}\n::SYS::\n${String(system ?? '')}\n::USR::\n${String(user ?? '')}`;
}

/** Estimativa de tokens a partir de chars (~4 chars/token), piso 0. */
export function estimateContextTokens(chars) {
  const n = Number(chars);
  return Number.isFinite(n) && n > 0 ? Math.ceil(n / 4) : 0;
}

// ── E3: status 4-valorado (derivado de sinais VERIFICÁVEIS, nunca auto-relato) ─

export const WORKER_STATUS = Object.freeze({
  DONE: 'DONE',
  DONE_WITH_CONCERNS: 'DONE_WITH_CONCERNS',
  NEEDS_CONTEXT: 'NEEDS_CONTEXT',
  BLOCKED: 'BLOCKED',
});

// stopReasons de RECURSO (o operário travou, não entregou) → BLOCKED.
const BLOCKED_STOP_REASONS = new Set(['tool_failure_spiral', 'llm_timeout', 'max_iterations', 'error']);
// Heurística NEEDS_CONTEXT: o operário sinalizou que faltou informação. São
// padrões de pedido-de-contexto no finalText (sinal verificável no texto que
// ELE produziu como resultado, não um campo de auto-avaliação que ele controla).
const NEEDS_CONTEXT_RE =
  /\b(preciso (?:d?os?|de) (?:arquivos?|contexto|acesso)|não (?:recebi|tenho|encontrei) (?:os?|o)? ?(?:arquivos?|contexto|código)|me (?:passe|envie|forneça)|faltou(?:-me)? (?:o )?contexto|sem acesso a)\b/i;
const MIN_SUBSTANTIAL_LEN = 40; // finalText abaixo disso = magro/truncado

/**
 * Deriva o status 4-valorado de um operário a partir de SINAIS VERIFICÁVEIS:
 *   - runState   (hermes_runs.state — autoridade server-side via CAS)
 *   - ok         (o loop terminou com sucesso?)
 *   - stopReason (motivo terminal do agentLoop)
 *   - citationCovered (a síntese conseguiu citar este operário?)
 *   - finalTextLen (tamanho do resultado)
 * NUNCA lê auto-avaliação do operário (report-como-dado, 06-REVIEW #6/#8).
 */
export function deriveWorkerStatus({
  runState,
  ok,
  stopReason,
  citationCovered = true,
  finalTextLen = 0,
  finalText = '',
} = {}) {
  const S = WORKER_STATUS;
  if (runState === 'cancelled' || stopReason === 'aborted') return S.BLOCKED;
  if (runState === 'failed' || ok === false) {
    if (BLOCKED_STOP_REASONS.has(stopReason)) return S.BLOCKED;
    return S.BLOCKED; // falha sem entrega utilizável
  }
  // A partir daqui o run terminou OK no ledger.
  if (typeof finalText === 'string' && NEEDS_CONTEXT_RE.test(finalText)) return S.NEEDS_CONTEXT;
  const len = Number(finalTextLen) || (typeof finalText === 'string' ? finalText.length : 0);
  if (len < MIN_SUBSTANTIAL_LEN) return S.DONE_WITH_CONCERNS; // truncado/magro
  if (!citationCovered) return S.DONE_WITH_CONCERNS; // a síntese não conseguiu usar
  return S.DONE;
}

// ── E4: context budget por dispatch (política sobre o injectionBudget LAW) ────

/**
 * Decide QUAIS blocos o operário recebe (briefing + recall relevante), nunca o
 * transcript inteiro, e delega o CORTE ao assembler do injectionBudget (LAW —
 * só consumir). `assemble` é injetado (= assembleWithinBudget) pra manter o
 * domain puro (sem import de infra/lib). Retorna { text, tokens, truncated }.
 */
export function capDispatchContext(blocks, { assemble, softCapTokens } = {}) {
  const ordered = (Array.isArray(blocks) ? blocks : []).filter(
    (b) => b && typeof b.content === 'string' && b.content.trim(),
  );
  if (typeof assemble !== 'function') {
    // Sem assembler injetado: concat simples (degrada, não trava).
    const text = ordered.map((b) => b.content).join('\n\n');
    return { text, tokens: estimateContextTokens(text.length), truncated: false };
  }
  return assemble(ordered, softCapTokens ? { softCapTokens } : {});
}
